import { useCopy, useLocaleDescriptor } from '@/i18n/useCopy'
import { useSessionStore } from '@/store/sessionStore'
import { describeVoiceError } from '@/voice/describeError'
import styles from './ErrorBanner.module.css'

interface ErrorBannerProps {
  /** Offered for anything the person can actually do something about. */
  onRetry?(): void
}

/**
 * What went wrong, and the way out.
 *
 * Errors here are directions, not apologies. Each one names what happened and
 * what to do next, because the failure this guards against is real: a voice
 * app that loses its connection and shows nothing looks exactly like a voice
 * app that is idle, and the person sits there talking to a dead orb.
 *
 * Not a live region. The single `role="alert"` in LiveRegions announces the
 * same words, from the same `describeVoiceError`, so the two can no longer
 * disagree about what happened or what language to say it in.
 */
export function ErrorBanner({ onRetry }: ErrorBannerProps) {
  const copy = useCopy()
  const locale = useLocaleDescriptor()
  const error = useSessionStore((state) => state.error)

  if (!error) return null

  const { title, body, retryable } = describeVoiceError(error.code, copy, locale)

  return (
    <div className={styles.banner} role="presentation">
      <span className={styles.title}>{title}</span>
      <span className={styles.body}>{body}</span>
      {retryable && onRetry ? (
        <button type="button" className={styles.action} onClick={onRetry}>
          {copy.ui.control.retry}
        </button>
      ) : null}
    </div>
  )
}
